/**
 * Archive ("several uploads as ONE sheet") merge core.
 *
 * CherryClipz opens ONE transcript at a time. The archive opens several as a single merged list
 * you filter/sort/search together, tick across, and let Black Cherry edit across. Uploads stay
 * 1-by-1; this module only MERGES already-loaded rows. It is PURE on purpose — no React, no
 * network — so the risky part (id namespacing) is unit-tested before it touches the live ET.
 *
 * THE CRUX — id namespacing: a clip `id` is a DB primary key unique only WITHIN one transcript,
 * and it is the join key across the whole app (selection sets, Black Cherry idMap round-trip,
 * adjusted times, split ids, backend `pk__in`). Two interviews can both have a clip id 456, so
 * every id becomes `${transcriptId}_${id}`. `parseSheetId` recovers the pair for the export.
 *
 * Why an UNDERSCORE (the July draft used a colon): the AI reply parsers tokenise ids with
 * `[A-Za-z0-9_-]+`, virality replies are `id:score` pairs, and split ids append `-a`. A colon
 * broke the first two; an underscore survives all three and JSON keeps it as a string.
 *
 * Speakers: AssemblyAI labels speakers per file, so "A" in one video is not "A" in another. The
 * merge keeps the raw letter on `rawSpeaker` and sets the DEFAULT label `A·1` (speaker · sheet).
 * Linking the same person across videos is the identity layer in speakerIdentity.js.
 */

export const SHEET_SEP = "_";

// Distinct, high-contrast source colours (the left stripe / badge per video). Cherry-red is
// reserved for selection state, so this palette avoids it. Cycles if more sheets than colours.
export const SOURCE_COLORS = [
  "#3b82f6", // blue
  "#f59e0b", // amber
  "#10b981", // emerald
  "#a855f7", // purple
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#6366f1", // indigo
];

export const sourceColorFor = (index) => SOURCE_COLORS[index % SOURCE_COLORS.length];

/** Short badge for a source by position: V1, V2, … (what the AI pack and the row badge show). */
export const shortLabelFor = (index) => `V${index + 1}`;

/** `456` (from transcript 6) -> `"6_456"`. */
export const makeSheetId = (transcriptId, rawId) => `${transcriptId}${SHEET_SEP}${rawId}`;

/**
 * Recover `{ transcriptId, rawId }` from a namespaced id — the seam the export needs to map each
 * ticked clip back to its own transcript + video. Splits on the FIRST underscore only, so a
 * split-piece suffix ("6_456-a") stays on `rawId`. Returns null for anything that is not a
 * namespaced id (a bare pk, a synthetic "iv-3"/"w-12" regroup id, nullish).
 */
export const parseSheetId = (sheetId) => {
  if (sheetId == null) return null;
  const s = String(sheetId);
  const i = s.indexOf(SHEET_SEP);
  if (i <= 0) return null;
  const transcriptId = s.slice(0, i);
  if (!/^\d+$/.test(transcriptId)) return null;
  return { transcriptId, rawId: s.slice(i + 1) };
};

/** Stable key for a combination of transcripts (order-independent): `[9, 6]` -> `"6-9"`. */
export const archiveKeyFor = (transcriptIds) =>
  [...new Set((transcriptIds || []).map((x) => String(x)))]
    .sort((a, b) => Number(a) - Number(b))
    .join("-");

/** Default per-source speaker label: "A·1" (speaker letter · sheet number). */
export const speakerLabelFor = (rawSpeaker, sheetNum) => `${rawSpeaker}·${sheetNum}`;

// Namespace one list of rows (any row type — sentence, entity, aib, …) for one source.
function namespaceRows(rows, source) {
  const out = [];
  (rows || []).forEach((row) => {
    if (!row) return;
    // Spacer rows (`space`) carry no id/speaker — tag the source so the source filter can drop
    // them with their sheet, but don't namespace fields they don't have.
    if (row.space) {
      out.push({ ...row, source });
      return;
    }
    // AssemblyAI labels sometimes arrive as "A " (trailing space) — trim so keys/labels stay clean.
    const rawSpeaker = typeof row.speaker === "string" ? row.speaker.trim() : row.speaker;
    const hasSpeaker = rawSpeaker != null && rawSpeaker !== "";
    out.push({
      ...row,
      pk: row.id,
      id: makeSheetId(source.transcriptId, row.id),
      ...(hasSpeaker
        ? { rawSpeaker, speaker: speakerLabelFor(rawSpeaker, source.sheetNum) }
        : {}),
      source,
    });
  });
  return out;
}

/**
 * Merge several already-loaded sheets into one ordered row list.
 *
 * @param {Array<{ transcriptId, label, rows, color?, video?, ext?, duration? }>} sheets
 * @returns {object[]} one concatenated list; each clip row gets:
 *   - `id`         namespaced `${transcriptId}_${originalId}`
 *   - `pk`         the original DB id (unchanged)
 *   - `rawSpeaker` the AssemblyAI letter; `speaker` = "A·1" default label
 *   - `source`     `{ transcriptId, label, short, color, sheetNum, video, ext, duration }`
 * Sheets are concatenated in the given order (scroll sheet 1 -> flows into sheet 2); the ET's
 * own sort/filter then mixes them, which is the desired behaviour.
 */
export function mergeSheets(sheets) {
  if (!Array.isArray(sheets)) return [];
  const out = [];
  sheets.forEach((sheet, index) => {
    if (!sheet || !Array.isArray(sheet.rows)) return;
    const source = makeSource(sheet, index);
    out.push(...namespaceRows(sheet.rows, source));
  });
  return out;
}

function makeSource(sheet, index) {
  return {
    transcriptId: sheet.transcriptId,
    label: sheet.label || sheet.title || `Transcript ${sheet.transcriptId}`,
    short: shortLabelFor(index),
    color: sheet.color || sourceColorFor(index),
    sheetNum: index + 1,
    video: sheet.video || "",
    ext: sheet.ext || "",
    duration: Number(sheet.duration) || 0,
    lazy: !!sheet.lazy,
    clips: Array.isArray(sheet.rows) ? sheet.rows.filter((r) => r && !r.space).length : 0,
  };
}

/**
 * Merge whole LOADED transcripts (every row type the stream carries) into the shape the ET
 * ingests. Input is what archiveLoader returns per transcript.
 *
 * @param {Array<{ transcriptId, title?, metadata?, sentences, entity, category, aib,
 *                 auto_highlight, content_moderation }>} loaded
 * @returns {{ sources, rows, entity, category, aib, auto_highlight, content_moderation,
 *             totalDuration, lazy, key }}
 */
export function mergeArchive(loaded) {
  const list = Array.isArray(loaded) ? loaded.filter(Boolean) : [];
  const sources = list.map((t, index) => {
    const md = t.metadata || {};
    return makeSource({
      transcriptId: t.transcriptId,
      label: t.title || md.title,
      rows: t.sentences,
      video: md.video,
      ext: md.ext,
      duration: md.video_duration,
      lazy: md.lazy_thumbnails,
    }, index);
  });
  const merged = { rows: [], entity: [], category: [], aib: [], auto_highlight: [], content_moderation: [] };
  list.forEach((t, index) => {
    const source = sources[index];
    merged.rows.push(...namespaceRows(t.sentences, source));
    merged.entity.push(...namespaceRows(t.entity, source));
    merged.category.push(...namespaceRows(t.category, source));
    merged.aib.push(...namespaceRows(t.aib, source));
    merged.auto_highlight.push(...namespaceRows(t.auto_highlight, source));
    merged.content_moderation.push(...namespaceRows(t.content_moderation, source));
  });
  return {
    sources,
    ...merged,
    totalDuration: sources.reduce((a, s) => a + (s.duration || 0), 0),
    lazy: sources.some((s) => s.lazy),
    key: archiveKeyFor(sources.map((s) => s.transcriptId)),
  };
}

/** True when two rows come from the same source (or when neither is archive-tagged). */
export const sameSource = (a, b) => {
  const sa = a && a.source ? a.source.transcriptId : null;
  const sb = b && b.source ? b.source.transcriptId : null;
  if (sa == null || sb == null) return true;
  return String(sa) === String(sb);
};
